import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import WebDevelopment from "./pages/WebDevelopment";
import SEO from "./pages/SEO";
import PaidAdvertising from "./pages/PaidAdvertising";
import SocialMedia from "./pages/SocialMedia";
import AIMarketing from "./pages/AIMarketing";
import Clients from "./pages/Clients";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import ReputationManagement from "./pages/ReputationManagement";
import Privacy from "./pages/Privacy";
import Accessibility from "./pages/Accessibility";
import ThankYou from "./pages/ThankYou";

// Admin
import { ProtectedRoute } from "./components/admin/ProtectedRoute";
import AdminLayout from "./pages/admin/AdminLayout";
import Login from "./pages/admin/Login";
import Dashboard from "./pages/admin/Dashboard";
import BlogPostList from "./pages/admin/BlogPostList";
import BlogPostForm from "./pages/admin/BlogPostForm";
import ProjectList from "./pages/admin/ProjectList";
import ProjectForm from "./pages/admin/ProjectForm";
import TestimonialList from "./pages/admin/TestimonialList";
import TestimonialForm from "./pages/admin/TestimonialForm";
import ClientList from "./pages/admin/ClientList";
import ClientForm from "./pages/admin/ClientForm";
import ContactSubmissions from "./pages/admin/ContactSubmissions";

function PublicRouter() {
  return (
    <Layout>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/about" component={About} />
        <Route path="/services" component={Services} />
        <Route path="/services/web-development" component={WebDevelopment} />
        <Route path="/services/seo" component={SEO} />
        <Route path="/services/paid-advertising" component={PaidAdvertising} />
        <Route path="/services/social-media" component={SocialMedia} />
        <Route path="/services/ai-marketing" component={AIMarketing} />
        <Route path="/services/reputation-management" component={ReputationManagement} />
        <Route path="/clients" component={Clients} />
        <Route path="/projects" component={Projects} />
        <Route path="/projects/:id" component={ProjectDetail} />
        <Route path="/blog" component={Blog} />
        <Route path="/blog/:slug" component={BlogPost} />
        <Route path="/contact" component={Contact} />
        <Route path="/thank-you" component={ThankYou} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/accessibility" component={Accessibility} />
        <Route path="/404" component={NotFound} />
        <Route component={NotFound} />
      </Switch>
    </Layout>
  );
}

function AdminRouter() {
  return (
    <Switch>
      <Route path="/admin/login" component={Login} />
      <Route>
        <ProtectedRoute>
          <AdminLayout>
            <Switch>
              <Route path="/admin" component={Dashboard} />
              <Route path="/admin/blog-posts" component={BlogPostList} />
              <Route path="/admin/blog-posts/:id" component={BlogPostForm} />
              <Route path="/admin/projects" component={ProjectList} />
              <Route path="/admin/projects/:id" component={ProjectForm} />
              <Route path="/admin/testimonials" component={TestimonialList} />
              <Route path="/admin/testimonials/:id" component={TestimonialForm} />
              <Route path="/admin/clients" component={ClientList} />
              <Route path="/admin/clients/:id" component={ClientForm} />
              <Route path="/admin/leads" component={ContactSubmissions} />
              <Route component={NotFound} />
            </Switch>
          </AdminLayout>
        </ProtectedRoute>
      </Route>
    </Switch>
  );
}

function Router() {
  const [location] = useLocation();
  const isAdmin = location.startsWith("/admin");

  if (isAdmin) return <AdminRouter />;
  return <PublicRouter />;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <TooltipProvider>
          <Toaster />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
